import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';

import { baermannApi } from '../api';
import type { Baermann, BaermannCreate, BaermannUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-baermann'] as const;

export const baermannKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useBaermannListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? baermannKeys.byProtocolo(uidProtocolo) : baermannKeys.all,
    queryFn: ({ signal }) => baermannApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useBaermann(
  uidProtocolo: string | undefined,
  uidAnalisis: string | undefined
) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis
        ? baermannKeys.detail(uidProtocolo, uidAnalisis)
        : baermannKeys.all,
    queryFn: ({ signal }) => baermannApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateBaermann() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      data,
    }: {
      uidProtocolo: string;
      data: BaermannCreate;
    }) => baermannApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: baermannKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useUpdateBaermann() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: BaermannUpdate;
    }) => baermannApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: Baermann, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({
        queryKey: baermannKeys.byProtocolo(uidProtocolo),
      });
      queryClient.setQueryData(baermannKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteBaermann() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
    }) => baermannApi.remove(uidProtocolo, uidAnalisis),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: baermannKeys.byProtocolo(uidProtocolo),
      });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}
